import { ScimSchemaService } from './ScimSchemaService';
import CentralizedSchemaService from './CentralizedSchemaService';
import { debugLog, debugVerboseLog } from '../utils/debugLog';

export interface SchemaEntityType {
    id: string;
    name: string;
    attributes: SchemaAttribute[];
}

export interface SchemaAttribute {
    name: string;
    type: string;
    description?: string;
    required?: boolean;
    multiValued?: boolean;
    caseExact?: boolean;
    mutability?: string;
    returned?: string;
    uniqueness?: string;
    subAttributes?: SchemaAttribute[];
}

export interface FieldOption {
    value: string;
    label: string;
    type: string;
    required: boolean;
    multiValued: boolean;
    isReference?: boolean;
    subAttributes?: SchemaAttribute[];
    /** Attribute name to use in SCIM filter (DisplayName); when set, used instead of value so filter uses display name not id. */
    filterName?: string;
}

class SchemaCacheService {
    private static instance: SchemaCacheService;
    private cache: {
        schemas: SchemaEntityType[] | null;
        lastFetch: number | null;
        isLoading: boolean;
        error: string | null;
    } = {
            schemas: null,
            lastFetch: null,
            isLoading: false,
            error: null
        };

    private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
    private schemaService: ScimSchemaService;

    private constructor() {
        this.schemaService = new ScimSchemaService();
    }

    public static getInstance(): SchemaCacheService {
        if (!SchemaCacheService.instance) {
            SchemaCacheService.instance = new SchemaCacheService();
        }
        return SchemaCacheService.instance;
    }

    /**
     * Get cached schema data, fetching from API if needed
     */
    public async getSchemas(): Promise<SchemaEntityType[]> {
        const now = Date.now();

        // Return cached data if it's still valid
        if (this.cache.schemas &&
            this.cache.lastFetch &&
            (now - this.cache.lastFetch) < this.CACHE_DURATION) {
            debugVerboseLog('📋 SchemaCacheService: Returning cached schema data');
            return this.cache.schemas;
        }

        // If already loading, wait for the current request
        if (this.cache.isLoading) {
            debugVerboseLog('📋 SchemaCacheService: Waiting for existing schema fetch...');
            return new Promise((resolve, reject) => {
                const checkCache = () => {
                    if (!this.cache.isLoading) {
                        if (this.cache.error) {
                            reject(new Error(this.cache.error));
                        } else {
                            resolve(this.cache.schemas || []);
                        }
                    } else {
                        setTimeout(checkCache, 100);
                    }
                };
                checkCache();
            });
        }

        // Fetch fresh data
        this.cache.isLoading = true;
        this.cache.error = null;

        try {
            debugLog('📋 SchemaCacheService: Fetching fresh schema data from CentralizedSchemaService...');
            const centralizedService = CentralizedSchemaService.getInstance();
            const schemas = await centralizedService.getSchemas();

            // Convert to SchemaEntityType format
            const extractedEntityTypes = schemas.map((schema: any) => ({
                id: schema.id,
                name: schema.name,
                attributes: schema.attributes || []
            }));

            debugLog('📋 SchemaCacheService: Extracted entity types from CentralizedSchemaService:', extractedEntityTypes);

            this.cache.schemas = extractedEntityTypes;
            this.cache.lastFetch = now;
            this.cache.error = null;

            return extractedEntityTypes;
        } catch (error) {
            console.error('❌ SchemaCacheService: Error fetching schema data:', error);
            this.cache.error = error instanceof Error ? error.message : 'Failed to load schema data';
            throw error;
        } finally {
            this.cache.isLoading = false;
        }
    }

    /**
     * Get field options for a specific entity type
     */
    public async getFieldOptions(entityType?: string): Promise<FieldOption[]> {
        const schemas = await this.getSchemas();

        if (schemas.length === 0) {
            return [];
        }

        // If we have a specific entity type, find its schema
        if (entityType) {
            const targetEntityType = schemas.find(et =>
                et.name.toLowerCase() === entityType.toLowerCase()
            );

            if (targetEntityType) {
                debugVerboseLog(`📋 SchemaCacheService: Using schema for entity type: ${entityType}`);
                return this.generateFieldOptions(targetEntityType);
            }
        }

        // If no specific entity type or not found, use all attributes from all entity types
        debugVerboseLog('📋 SchemaCacheService: Using all schemas for field options');
        const allAttributes = new Set<string>();
        const attributeMap = new Map<string, FieldOption>();

        schemas.forEach(schema => {
            schema.attributes.forEach(attr => {
                if (!allAttributes.has(attr.name)) {
                    allAttributes.add(attr.name);
                    attributeMap.set(attr.name, {
                        value: attr.name,
                        label: attr.name.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()),
                        type: attr.type,
                        required: attr.required || false,
                        multiValued: attr.multiValued || false,
                        isReference: attr.type === 'reference',
                        subAttributes: attr.subAttributes || []
                    });
                }

                // Add reference sub-attributes
                if (attr.type === 'reference' && attr.subAttributes && attr.subAttributes.length > 0) {
                    attr.subAttributes.forEach(subAttr => {
                        const nestedFieldName = `${attr.name}.${subAttr.name}`;
                        if (!allAttributes.has(nestedFieldName)) {
                            allAttributes.add(nestedFieldName);
                            attributeMap.set(nestedFieldName, {
                                value: nestedFieldName,
                                label: `${attr.name.replace(/([A-Z])/g, ' $1').replace(/^./, (str: string) => str.toUpperCase())} - ${subAttr.name.replace(/([A-Z])/g, ' $1').replace(/^./, (str: string) => str.toUpperCase())}`,
                                type: subAttr.type,
                                required: subAttr.required || false,
                                multiValued: subAttr.multiValued || false,
                                isReference: false,
                                subAttributes: []
                            });
                        }
                    });
                }
            });
        });

        return Array.from(attributeMap.values()).sort((a, b) => a.label.localeCompare(b.label));
    }

    /**
     * Generate field options from a schema entity type
     */
    private generateFieldOptions(schemaEntityType: SchemaEntityType): FieldOption[] {
        const options: FieldOption[] = [];

        schemaEntityType.attributes.forEach(attr => {
            // Add the main attribute
            options.push({
                value: attr.name,
                label: attr.name.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()),
                type: attr.type,
                required: attr.required || false,
                multiValued: attr.multiValued || false,
                isReference: attr.type === 'reference',
                subAttributes: attr.subAttributes || [],
                filterName: attr.name
            });

            // If it's a reference type with sub-attributes, add nested field options
            if (attr.type === 'reference' && attr.subAttributes && attr.subAttributes.length > 0) {
                attr.subAttributes.forEach(subAttr => {
                    options.push({
                        value: `${attr.name}.${subAttr.name}`,
                        label: `${attr.name.replace(/([A-Z])/g, ' $1').replace(/^./, (str: string) => str.toUpperCase())} - ${subAttr.name.replace(/([A-Z])/g, ' $1').replace(/^./, (str: string) => str.toUpperCase())}`,
                        type: subAttr.type,
                        required: subAttr.required || false,
                        multiValued: subAttr.multiValued || false,
                        isReference: false,
                        subAttributes: [],
                        filterName: `${attr.name}.${subAttr.name}`
                    });
                });
            }
        });

        return options.sort((a, b) => a.label.localeCompare(b.label));
    }

    /**
     * Clear the cache to force a fresh fetch
     */
    public clearCache(): void {
        debugLog('🗑️ SchemaCacheService: Clearing schema cache');
        this.cache.schemas = null;
        this.cache.lastFetch = null;
        this.cache.error = null;
    }

    /**
     * Get cache status
     */
    public getCacheStatus(): {
        hasData: boolean;
        isLoading: boolean;
        error: string | null;
        lastFetch: number | null;
    } {
        return {
            hasData: this.cache.schemas !== null,
            isLoading: this.cache.isLoading,
            error: this.cache.error,
            lastFetch: this.cache.lastFetch
        };
    }
}

export default SchemaCacheService;
